import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";

import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";
import { AgeVerification } from "./components/AgeVerification";
import { RouteIndexing } from "./components/RouteIndexing";
import { getRoutePolicy } from "./lib/routePolicy";
import routeManifest from "./data/routeManifest.json";
import NotFound from "./pages/NotFound";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Livraison from "./pages/Livraison";
import CityLanding from "./pages/CityLanding";
import { CITIES } from "./data/cities";
import Flyer from "./pages/Flyer";
import AlcoholLanding from "./pages/AlcoholLanding";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminSettings from "./pages/admin/AdminSettings";

const Layout = ({ children }) => {
  const location = useLocation();
  const policy = getRoutePolicy(location.pathname);
  const isNotFound = policy.status === 404;
  const isAdmin = policy.path.startsWith("/admin");
  const isCityLanding = policy.path.startsWith("/livraison-");
  // Only /livraison exact — city landings (/livraison-*) are handled separately
  const isGoogleAdsLanding = policy.path === "/livraison";
  const isFlyer = policy.path === "/flyer";
  const hideChrome = isAdmin || isGoogleAdsLanding || isCityLanding || isFlyer;
  // Age gate required by French law (L3342-1 alcool, L3512-2 tabac) on any page that promotes these products.
  // City landings mention champagne/spiritueux/tabac → age gate MUST show.
  // /livraison (Google Ads landing) has no alcohol/tobacco mentions → keep hidden for Ads policy.
  // /admin and /flyer are internal → skip.
  const hideAgeGate = isAdmin || isGoogleAdsLanding || isFlyer || isNotFound;

  return (
    <>
      {!hideAgeGate && <AgeVerification />}
      {!hideChrome && <Navbar />}
      <main className="min-h-screen">{isNotFound ? <NotFound /> : children}</main>
      {!hideChrome && <Footer />}
      {!hideChrome && <CartDrawer />}
    </>
  );
};

const CategoryRoute = () => {
  const { category } = useParams();
  return routeManifest.categories.includes(category) ? <Shop /> : <NotFound />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteIndexing />
        <AuthProvider>
          <CartProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/livraison" element={<Livraison />} />
                <Route path="/livraison-alcool-24h24" element={<AlcoholLanding />} />
                <Route path="/livraison-alcool-24h24.html" element={<Navigate to="/livraison-alcool-24h24" replace />} />
                {Object.keys(CITIES).map((slug) => (
                  <Route
                    key={slug}
                    path={`/livraison-${slug}`}
                    element={<CityLanding />}
                  />
                ))}
                <Route path="/flyer" element={<Flyer />} />
                <Route path="/shop/:category" element={<CategoryRoute />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
            <Toaster theme="dark" position="bottom-right" />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
